import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Shield, Info, Download } from 'lucide-react';

const RiskMetricsPage = () => {
  const [amcList, setAmcList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [schemeData, setSchemeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAmc, setSelectedAmc] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedIndicator, setSelectedIndicator] = useState('default');
  const [sortConfig, setSortConfig] = useState({ key: 'sharpe', direction: 'desc' });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const indicators = [
    { value: 'default', label: 'All Indicators' },
    { value: 'sd', label: 'Standard Deviation' },
    { value: 'beta', label: 'Beta' },
    { value: 'alpha', label: 'Alpha' },
    { value: 'jensens_alpha', label: "Jensen's Alpha" },
    { value: 'sharpe', label: 'Sharpe Ratio' },
    { value: 'treynor', label: 'Treynor Ratio' },
    { value: 'sortino', label: 'Sortino Ratio' }
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchSchemeData();
  }, [selectedAmc, selectedCategory]);

  const fetchInitialData = async () => {
    try {
      const [amcRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/research/amc-list`),
        fetch(`${API_URL}/api/research/scheme-categories`)
      ]);
      
      const amcData = await amcRes.json();
      const catData = await catRes.json();
      
      if (amcData.status === 'success') setAmcList(amcData.data);
      if (catData.status === 'success') setCategories(catData.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchSchemeData = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/research/risk-metrics?amc=${selectedAmc}&category=${selectedCategory}`
      );
      const data = await res.json();
      if (data.status === 'success') {
        setSchemeData(data.data);
      }
    } catch (error) {
      console.error('Error fetching scheme data:', error);
    }
    setLoading(false);
  };

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = [...schemeData].sort((a, b) => {
    const aVal = a[sortConfig.key] || 0;
    const bVal = b[sortConfig.key] || 0;
    return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const formatValue = (value, key) => {
    if (value === null || value === undefined) return '-';
    const num = parseFloat(value);
    
    // Color coding based on metric type
    let color = 'text-white';
    if (key === 'sharpe' || key === 'treynor' || key === 'sortino' || key === 'alpha' || key === 'jensens_alpha') {
      color = num >= 0 ? 'text-green-400' : 'text-red-400';
    } else if (key === 'beta') {
      color = num <= 1 ? 'text-green-400' : 'text-yellow-400';
    } else if (key === 'sd') {
      color = num <= 12 ? 'text-green-400' : num <= 16 ? 'text-yellow-400' : 'text-red-400';
    }
    
    return <span className={color}>{num.toFixed(2)}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      
      <div className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl mb-4">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Risk Metrics Analysis
            </h1>
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 max-w-3xl mx-auto">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                <p className="text-white/80 text-sm text-left">
                  Analyze risk and risk-adjusted returns for mutual fund schemes. 
                  All calculations are based on <strong>3 Year Period</strong> data.
                </p>
              </div>
            </div>
          </div>

          {/* Metric Explanations */}
          <div className="grid md:grid-cols-4 gap-3 mb-6">
            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
              <p className="text-purple-400 font-semibold text-sm">Standard Deviation</p>
              <p className="text-white/60 text-xs mt-1">Measures volatility - lower is more stable</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
              <p className="text-purple-400 font-semibold text-sm">Sharpe Ratio</p>
              <p className="text-white/60 text-xs mt-1">Risk-adjusted return - higher is better</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
              <p className="text-purple-400 font-semibold text-sm">Beta</p>
              <p className="text-white/60 text-xs mt-1">Market sensitivity - 1 = market movement</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3 border border-white/10">
              <p className="text-purple-400 font-semibold text-sm">Alpha</p>
              <p className="text-white/60 text-xs mt-1">Excess return vs benchmark - positive is better</p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 mb-6">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-white/70 text-sm mb-2 block">Select Fund House</label>
                <select
                  value={selectedAmc}
                  onChange={(e) => setSelectedAmc(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500 appearance-none cursor-pointer"
                >
                  <option value="all" className="bg-gray-900">All Fund Houses</option>
                  {amcList.map((amc, idx) => (
                    <option key={idx} value={amc} className="bg-gray-900">{amc}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-white/70 text-sm mb-2 block">Select Scheme Type</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500 appearance-none cursor-pointer"
                >
                  {categories.map((cat, idx) => (
                    <option key={idx} value={cat.value} className="bg-gray-900">{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-white/70 text-sm mb-2 block">Select Indicator</label>
                <select
                  value={selectedIndicator}
                  onChange={(e) => setSelectedIndicator(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500 appearance-none cursor-pointer"
                >
                  {indicators.map((ind, idx) => (
                    <option key={idx} value={ind.value} className="bg-gray-900">{ind.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="flex justify-between items-center mb-4">
            <p className="text-white/60 text-sm">
              Showing <span className="text-white font-semibold">{sortedData.length}</span> schemes
            </p>
            <button className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-lg text-purple-400 hover:bg-purple-500/30 transition-colors text-sm">
              <Download className="w-4 h-4" />
              Export Data
            </button>
          </div>

          {/* Data Table */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-white/5 border-b border-white/10">
                    <th className="text-left py-4 px-4 text-white/70 font-medium text-sm">Scheme Name</th>
                    <th className="text-right py-4 px-3 text-white/70 font-medium text-sm cursor-pointer hover:text-white" onClick={() => handleSort('sd')}>
                      SD {sortConfig.key === 'sd' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="text-right py-4 px-3 text-white/70 font-medium text-sm cursor-pointer hover:text-white" onClick={() => handleSort('beta')}>
                      Beta {sortConfig.key === 'beta' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="text-right py-4 px-3 text-white/70 font-medium text-sm cursor-pointer hover:text-white" onClick={() => handleSort('alpha')}>
                      Alpha {sortConfig.key === 'alpha' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="text-right py-4 px-3 text-white/70 font-medium text-sm cursor-pointer hover:text-white" onClick={() => handleSort('jensens_alpha')}>
                      Jensen&apos;s α {sortConfig.key === 'jensens_alpha' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="text-right py-4 px-3 text-white/70 font-medium text-sm cursor-pointer hover:text-white" onClick={() => handleSort('sharpe')}>
                      Sharpe {sortConfig.key === 'sharpe' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="text-right py-4 px-3 text-white/70 font-medium text-sm cursor-pointer hover:text-white" onClick={() => handleSort('treynor')}>
                      Treynor {sortConfig.key === 'treynor' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                    <th className="text-right py-4 px-3 text-white/70 font-medium text-sm cursor-pointer hover:text-white" onClick={() => handleSort('sortino')}>
                      Sortino {sortConfig.key === 'sortino' && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="text-center py-12 text-white/50">
                        <div className="flex items-center justify-center gap-3">
                          <div className="w-6 h-6 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
                          Loading risk metrics...
                        </div>
                      </td>
                    </tr>
                  ) : sortedData.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-12 text-white/50">
                        No schemes found for the selected filters
                      </td>
                    </tr>
                  ) : (
                    sortedData.map((scheme, idx) => (
                      <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-4">
                          <div>
                            <p className="text-white font-medium text-sm">{scheme.scheme_name}</p>
                            <p className="text-white/50 text-xs mt-1">{scheme.amc}</p>
                          </div>
                        </td>
                        <td className="text-right py-4 px-3 font-mono text-sm">{formatValue(scheme.sd, 'sd')}</td>
                        <td className="text-right py-4 px-3 font-mono text-sm">{formatValue(scheme.beta, 'beta')}</td>
                        <td className="text-right py-4 px-3 font-mono text-sm">{formatValue(scheme.alpha, 'alpha')}</td>
                        <td className="text-right py-4 px-3 font-mono text-sm">{formatValue(scheme.jensens_alpha, 'jensens_alpha')}</td>
                        <td className="text-right py-4 px-3 font-mono text-sm">{formatValue(scheme.sharpe, 'sharpe')}</td>
                        <td className="text-right py-4 px-3 font-mono text-sm">{formatValue(scheme.treynor, 'treynor')}</td>
                        <td className="text-right py-4 px-3 font-mono text-sm">{formatValue(scheme.sortino, 'sortino')}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-6 text-center">
            <p className="text-white/40 text-xs">
              * Risk metrics are calculated using 3-year historical data. Higher Sharpe/Sortino ratios indicate better risk-adjusted returns.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RiskMetricsPage;
